import { APPLICATION_CODES, UserV2 } from "@/types";
import {
  FAST_MK2_ROLES,
  PLATFORM_AGILE_ADMIN_PERMISSIONS,
  PLATFORM_AGILE_ADVANCED_FLEXPERT_SUPPORT_PERMISSIONS,
  PLATFORM_AGILE_REGIONAL_LEAD_PERMISSIONS,
  PLATFORM_AGILE_ROLES,
  PLATFORM_SUPER_ADMIN,
} from "@/types/userV2Permissions";
import { APPLICATION_NAMES } from "@/types";

export const hasRequiredPermissions = (
  user: UserV2,
  currentUserRole: string,
  requiredPermissions: string[]
) => {
  const userRole = user.role_associations.find(
    (role) => role.code === currentUserRole
  );

  if (!userRole) return false;

  const rolePermissions = userRole.permissions.map(
    (permission) => permission.code
  );

  return requiredPermissions?.every((permission) =>
    rolePermissions.includes(permission)
  );
};

export const haveCommonRegions = (
  targetUser: UserV2,
  currentUserAgileRegions?: {
    label: string;
    value: number;
  }[]
) => {
  const targetUserAgileRole = targetUser.role_associations.find(
    (role) => role.application === APPLICATION_NAMES.FAST_MK2
  );

  if (!targetUserAgileRole || !currentUserAgileRegions) return false;

  const targetUserAgileRoleTags = targetUserAgileRole.tags.map(
    (tag) => tag.name
  );

  if (!targetUserAgileRoleTags?.length) return false;

  return currentUserAgileRegions.some((tag) =>
    targetUserAgileRoleTags.includes(tag.label)
  );
};

export const isAbleToActOnUser = (
  targetUser: UserV2,
  user: UserV2,
  isPlatformSuperAdmin: boolean,
  currentUserAgileRegions?: {
    label: string;
    value: number;
  }[]
) => {
  if (isPlatformSuperAdmin) return true;

  const isAgileUser = targetUser.applications.some(
    (application) => application.code === APPLICATION_CODES.FAST_MK2
  );

  if (!isAgileUser) return false;

  const isFlexpertTargetUser = targetUser.role_associations.some(
    (role) => role.code === FAST_MK2_ROLES.FLEXPERT
  );

  const isFlexpertSupportTargetUser = targetUser.role_associations.some(
    (role) => role.code === FAST_MK2_ROLES.FLEXPERT_SUPPORT
  );

  const isAgileAdminTargetUser = targetUser.role_associations.some(
    (role) => role.code === PLATFORM_AGILE_ROLES.AGILE_ADMIN
  );

  const isAdminTargetUser = targetUser.role_associations.some(
    (role) => role.code === FAST_MK2_ROLES.ADMIN
  );

  const isAgileAdminAbleToEdit =
    !isAgileAdminTargetUser &&
    !isAdminTargetUser &&
    haveCommonRegions(targetUser, currentUserAgileRegions) &&
    hasRequiredPermissions(user, PLATFORM_AGILE_ROLES.AGILE_ADMIN, [
      PLATFORM_AGILE_ADMIN_PERMISSIONS.DEACTIVATE_AGILE_USERS,
      PLATFORM_AGILE_ADMIN_PERMISSIONS.UPDATE_AGILE_USERS,
    ]);

  const isRegionalLeadAbleToEdit =
    isFlexpertTargetUser &&
    haveCommonRegions(targetUser, currentUserAgileRegions) &&
    hasRequiredPermissions(user, PLATFORM_AGILE_ROLES.AGILE_REGIONAL_LEAD, [
      PLATFORM_AGILE_REGIONAL_LEAD_PERMISSIONS.DEACTIVATE_FLEXPERT_USERS,
      PLATFORM_AGILE_REGIONAL_LEAD_PERMISSIONS.UPDATE_FLEXPERT_USERS,
    ]);

  const isAdvancedFlexpertSupportAbleToEdit =
    isFlexpertSupportTargetUser &&
    haveCommonRegions(targetUser, currentUserAgileRegions) &&
    hasRequiredPermissions(
      user,
      PLATFORM_AGILE_ROLES.AGILE_ADVANCED_FLEXPERT_SUPPORT,
      [
        PLATFORM_AGILE_ADVANCED_FLEXPERT_SUPPORT_PERMISSIONS.DEACTIVATE_FLEXPERT_SUPPORT_USERS,
        PLATFORM_AGILE_ADVANCED_FLEXPERT_SUPPORT_PERMISSIONS.UPDATE_FLEXPERT_SUPPORT_USERS,
      ]
    );

  return (
    isAgileAdminAbleToEdit ||
    isRegionalLeadAbleToEdit ||
    isAdvancedFlexpertSupportAbleToEdit
  );
};

export const isAbleToCreateUser = (user: UserV2 | null) => {
  if (!user) {
    return;
  }
  const isPlatformSuperAdmin = user.role_associations.some(
    (role) => role.code === PLATFORM_SUPER_ADMIN
  );

  if (isPlatformSuperAdmin) return true;

  const isAgileAdminAbleToCreateUsers = hasRequiredPermissions(
    user,
    PLATFORM_AGILE_ROLES.AGILE_ADMIN,
    [PLATFORM_AGILE_ADMIN_PERMISSIONS.CREATE_AGILE_USERS]
  );

  const isRegionalLeadAbleToCreateUsers = hasRequiredPermissions(
    user,
    PLATFORM_AGILE_ROLES.AGILE_REGIONAL_LEAD,
    [PLATFORM_AGILE_REGIONAL_LEAD_PERMISSIONS.CREATE_FLEXPERT_USERS]
  );

  const isAdvancedFlexpertSupportAbleToCreateUsers = hasRequiredPermissions(
    user,
    PLATFORM_AGILE_ROLES.AGILE_ADVANCED_FLEXPERT_SUPPORT,
    [
      PLATFORM_AGILE_ADVANCED_FLEXPERT_SUPPORT_PERMISSIONS.CREATE_FLEXPERT_SUPPORT_USERS,
    ]
  );

  return (
    isAgileAdminAbleToCreateUsers ||
    isRegionalLeadAbleToCreateUsers ||
    isAdvancedFlexpertSupportAbleToCreateUsers
  );
};

export const isAbleToActivateUser = (targetUser: UserV2, user: UserV2) => {
  const isAdminUser = user.role_associations.some(
    (role) => role.code === FAST_MK2_ROLES.ADMIN
  );

  const isAdvancedFlexpertSupportTargetUser = targetUser.role_associations.some(
    (role) => role.code === PLATFORM_AGILE_ROLES.AGILE_ADVANCED_FLEXPERT_SUPPORT
  );

  const isRegionalLeadTargetUser = targetUser.role_associations.some(
    (role) => role.code === PLATFORM_AGILE_ROLES.AGILE_REGIONAL_LEAD
  );

  const isAdminAbleToActivate =
    isAdminUser &&
    (isAdvancedFlexpertSupportTargetUser || isRegionalLeadTargetUser);

  return isAdminAbleToActivate;
};
